import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Summarize as SummaryIcon,
  Sms as SmsIcon,
  Update as UpdateIcon,
  Description as DescriptionIcon,
  Link as LinkIcon,
  Send as SendIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarTodayIcon,
  Event as EventIcon,
  EventAvailable as EventAvailableIcon,
  EventBusy as EventBusyIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { RenewalDashboardMetrics, RenewalDashboardFilterOptions } from '@/types/renewalDashboard';
import { downloadSmsMetricsTransactions, downloadRenewalDashboardSummary } from '@/services/api';
import { formatErrorForDisplay } from '@/utils/errorUtils';

interface RenewalMetricsCardsProps {
  metrics: RenewalDashboardMetrics | null;
  loading?: boolean;
  error?: string | null;
  filters?: RenewalDashboardFilterOptions;
}

interface MetricTileProps {
  label: string;
  value: number | string;
  icon?: React.ReactNode;
  color?: string;
  bgColor?: string;
  borderColor?: string;
  isPrimary?: boolean;
}

const MetricTile: React.FC<MetricTileProps> = ({ 
  label, 
  value, 
  icon, 
  color = '#003087',
  bgColor = 'rgba(0, 48, 135, 0.05)',
  borderColor = 'rgba(0, 48, 135, 0.2)',
  isPrimary = false
}) => (
  <Box
    sx={{
      p: 0.75,
      borderRadius: 1.5,
      backgroundColor: bgColor,
      borderLeft: `3px solid ${borderColor}`,
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: bgColor.replace('0.05', '0.1'),
        transform: 'translateX(2px)',
        boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
      }
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1 }}>
        {icon && (
          <Box sx={{ color, opacity: 0.8, display: 'flex', alignItems: 'center' }}>
            {icon}
          </Box>
        )}
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'text.secondary',
            fontWeight: 500,
            fontSize: '0.75rem', // Increased from 0.65rem for better readability at 150% zoom
            lineHeight: 1.3,
            wordBreak: 'break-word',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
    <Typography 
      variant={isPrimary ? "h5" : "h6"}
      sx={{ 
        color,
        fontWeight: 700,
        letterSpacing: '-0.01em',
        lineHeight: 1.2,
        fontSize: isPrimary ? '1.3125rem' : '1.125rem' // Adjusted for 150% zoom compensation
      }}
    >
      {typeof value === 'number' ? value.toLocaleString() : value}
    </Typography>
  </Box>
);

const RenewalMetricsCards: React.FC<RenewalMetricsCardsProps> = ({ metrics, loading = false, error = null, filters = {} }) => {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadingSummary, setDownloadingSummary] = useState(false);
  const [downloadSummaryError, setDownloadSummaryError] = useState<string | null>(null);

  const handleDownload = async () => {
    if (!filters) {
      setDownloadError('No filters available for download');
      return;
    }

    setDownloading(true);
    setDownloadError(null);

    try {
      await downloadSmsMetricsTransactions(filters);
    } catch (err: any) {
      setDownloadError(err.message || 'Failed to download SMS metrics transactions');
      console.error('Download error:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadSummary = async () => {
    if (!filters) {
      setDownloadSummaryError('No filters available for download');
      return;
    }

    setDownloadingSummary(true);
    setDownloadSummaryError(null);

    try {
      await downloadRenewalDashboardSummary(filters);
    } catch (err: any) {
      setDownloadSummaryError(err.message || 'Failed to download summary');
      console.error('Download summary error:', err);
    } finally {
      setDownloadingSummary(false);
    }
  };
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading metrics...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {formatErrorForDisplay(error)}
      </Alert>
    );
  }

  if (!metrics) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No metrics available
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={1.5}>
      {/* Summary */}
      <Grid item xs={12} sm={12} md={3}>
        <Card 
          sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 3,
            borderRadius: 2,
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: 6,
              transform: 'translateY(-2px)'
            }
          }}
        >
          <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 1.5,
                pb: 1,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SummaryIcon color="primary" sx={{ fontSize: 20 }} /> {/* Adjusted for 150% zoom */}
                <Typography variant="h6" sx={{ ml: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                  Summary
                </Typography>
              </Box>
              <Tooltip title="Download Summary as Excel">
                <IconButton
                  size="small"
                  onClick={handleDownloadSummary}
                  disabled={downloadingSummary}
                  sx={{ color: 'primary.main' }}
                >
                  {downloadingSummary ? <CircularProgress size={20} /> : <FileDownloadIcon />}
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
              <Box sx={{ pb: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                <MetricTile
                  label="Total Policies Uploaded"
                  value={metrics.summary.total_policies_uploaded}
                  icon={<DescriptionIcon sx={{ fontSize: 12 }} />}
                  color="#003087"
                  bgColor="rgba(0, 48, 135, 0.08)"
                  borderColor="#003087"
                  isPrimary={true}
                />
              </Box>
              <Box sx={{ pb: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                <MetricTile
                  label="Policies With SMS Sent"
                  value={metrics.summary.policies_with_sms_sent}
                  icon={<SendIcon sx={{ fontSize: 12 }} />}
                  color="#28a745"
                  bgColor="rgba(40, 167, 69, 0.08)"
                  borderColor="#28a745"
                />
              </Box>
              <MetricTile
                label="Policies Without SMS"
                value={metrics.summary.policies_without_sms}
                icon={<BlockIcon sx={{ fontSize: 12 }} />}
                color="#ff9800"
                bgColor="rgba(255, 152, 0, 0.08)"
                borderColor="#ff9800"
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Expiry Breakdown */}
      <Grid item xs={12} sm={6} md={4}>
        <Card 
          sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 3,
            borderRadius: 2,
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: 6,
              transform: 'translateY(-2px)'
            }
          }}
        >
          <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5,
                pb: 1,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
              }}
            >
              <UpdateIcon color="info" sx={{ fontSize: 20 }} /> {/* Adjusted for 150% zoom */}
              <Typography variant="h6" sx={{ ml: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                Expiry Breakdown
              </Typography>
            </Box>
            <Grid container spacing={1} sx={{ flex: 1 }}>
              <Grid item xs={6}>
                <MetricTile
                  label="Expired in 1 Day"
                  value={metrics.expiry_breakdown.expired_in_1_day}
                  icon={<EventBusyIcon sx={{ fontSize: 12 }} />}
                  color="#dc3545"
                  bgColor="rgba(220, 53, 69, 0.08)"
                  borderColor="#dc3545"
                />
              </Grid>
              <Grid item xs={6}>
                <MetricTile
                  label="Expired in 7 Days"
                  value={metrics.expiry_breakdown.expired_in_7_days}
                  icon={<EventIcon sx={{ fontSize: 12 }} />}
                  color="#ff6b6b"
                  bgColor="rgba(255, 107, 107, 0.08)"
                  borderColor="#ff6b6b"
                />
              </Grid>
              <Grid item xs={6}>
                <MetricTile
                  label="Expired in 15 Days"
                  value={metrics.expiry_breakdown.expired_in_15_days}
                  icon={<ScheduleIcon sx={{ fontSize: 12 }} />}
                  color="#ffa726"
                  bgColor="rgba(255, 167, 38, 0.08)"
                  borderColor="#ffa726"
                />
              </Grid>
              <Grid item xs={6}>
                <MetricTile
                  label="More than a Month"
                  value={metrics.expiry_breakdown.expired_in_1_month}
                  icon={<CalendarTodayIcon sx={{ fontSize: 12 }} />}
                  color="#ffeb3b"
                  bgColor="rgba(255, 235, 59, 0.08)"
                  borderColor="#ffeb3b"
                />
              </Grid>
              <Grid item xs={12}>
                <MetricTile
                  label="After 1 Month"
                  value={metrics.expiry_breakdown.expired_after_1_month}
                  icon={<EventAvailableIcon sx={{ fontSize: 12 }} />}
                  color="#66bb6a"
                  bgColor="rgba(102, 187, 106, 0.08)"
                  borderColor="#66bb6a"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* SMS Metrics */}
      <Grid item xs={12} sm={6} md={5}>
        <Card 
          sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 3,
            borderRadius: 2,
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: 6,
              transform: 'translateY(-2px)'
            }
          }}
        >
          <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 1.5,
                pb: 1,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SmsIcon color="success" sx={{ fontSize: 20 }} /> {/* Adjusted for 150% zoom */}
                <Typography variant="h6" sx={{ ml: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                  SMS Metrics
                </Typography>
              </Box>
              <Tooltip title="Download transaction data">
                <IconButton
                  size="small"
                  onClick={handleDownload}
                  disabled={downloading || loading}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'primary.main',
                      backgroundColor: 'rgba(0, 48, 135, 0.08)'
                    }
                  }}
                >
                  {downloading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <FileDownloadIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
            {downloadError && (
              <Alert severity="error" sx={{ mb: 1, fontSize: '0.75rem' }} onClose={() => setDownloadError(null)}>
                {downloadError}
              </Alert>
            )}
            <Grid container spacing={1} sx={{ flex: 1 }}>
              {/* Primary metric - Total SMS Attempted with Success/Failure inline */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 0.75,
                    borderRadius: 1.5,
                    backgroundColor: "rgba(0, 48, 135, 0.08)",
                    borderLeft: "3px solid #003087",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1 }}>
                    <Box sx={{ color: "#003087", opacity: 0.8, display: 'flex', alignItems: 'center' }}>
                      <SendIcon sx={{ fontSize: 12 }} />
                    </Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        lineHeight: 1.3
                      }}
                    >
                      Total SMS Attempted
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography 
                      variant="h5"
                      sx={{ 
                        color: "#003087",
                        fontWeight: 700,
                        letterSpacing: '-0.01em',
                        lineHeight: 1.2,
                        fontSize: '1.3125rem'
                      }}
                    >
                      {metrics.sms_metrics.total_sms_sent.toLocaleString()}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckCircleIcon sx={{ fontSize: 14, color: "#28a745" }} />
                        <Typography variant="body2" sx={{ color: "#28a745", fontWeight: 600, fontSize: '0.75rem' }}>
                          Success: {metrics.sms_metrics.success_count}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ErrorIcon sx={{ fontSize: 14, color: "#dc3545" }} />
                        <Typography variant="body2" sx={{ color: "#dc3545", fontWeight: 600, fontSize: '0.75rem' }}>
                          Failure: {metrics.sms_metrics.failure_count}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              
              {/* Notice Only Section */}
              <Grid item xs={12}>
                <Box sx={{ 
                  border: '1px solid rgba(0, 0, 0, 0.1)', 
                  borderRadius: 1, 
                  p: 1,
                  backgroundColor: 'rgba(76, 175, 80, 0.03)'
                }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, fontSize: '0.75rem', color: '#4CAF50' }}>
                    Notice Only
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6} sm={2}>
                      <MetricTile
                        label="Total"
                        value={metrics.sms_metrics.download_notice_only_count}
                        icon={<DescriptionIcon sx={{ fontSize: 12 }} />}
                        color="#4CAF50"
                        bgColor="rgba(76, 175, 80, 0.08)"
                        borderColor="#4CAF50"
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <MetricTile
                        label="Success"
                        value={metrics.sms_metrics.notice_only_success_count || 0}
                        icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
                        color="#28a745"
                        bgColor="rgba(40, 167, 69, 0.08)"
                        borderColor="#28a745"
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <MetricTile
                        label="Success %"
                        value={metrics.sms_metrics.notice_only_success_rate || "0.0%"}
                        icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
                        color="#28a745"
                        bgColor="rgba(40, 167, 69, 0.08)"
                        borderColor="#28a745"
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <MetricTile
                        label="Failure"
                        value={metrics.sms_metrics.notice_only_failure_count || 0}
                        icon={<ErrorIcon sx={{ fontSize: 12 }} />}
                        color="#dc3545"
                        bgColor="rgba(220, 53, 69, 0.08)"
                        borderColor="#dc3545"
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <MetricTile
                        label="Failure %"
                        value={metrics.sms_metrics.notice_only_failure_rate || "0.0%"}
                        icon={<ErrorIcon sx={{ fontSize: 12 }} />}
                        color="#dc3545"
                        bgColor="rgba(220, 53, 69, 0.08)"
                        borderColor="#dc3545"
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              
              {/* Notice + Link Section */}
              <Grid item xs={12}>
                <Box sx={{ 
                  border: '1px solid rgba(0, 0, 0, 0.1)', 
                  borderRadius: 1, 
                  p: 1,
                  backgroundColor: 'rgba(25, 118, 210, 0.03)'
                }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, fontSize: '0.75rem', color: '#1976D2' }}>
                    Notice + Link
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6} sm={2}>
                      <MetricTile
                        label="Total"
                        value={metrics.sms_metrics.notice_with_link_count}
                        icon={<DescriptionIcon sx={{ fontSize: 12 }} />}
                        color="#1976D2"
                        bgColor="rgba(25, 118, 210, 0.08)"
                        borderColor="#1976D2"
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <MetricTile
                        label="Success"
                        value={metrics.sms_metrics.notice_with_link_success_count || 0}
                        icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
                        color="#28a745"
                        bgColor="rgba(40, 167, 69, 0.08)"
                        borderColor="#28a745"
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <MetricTile
                        label="Success %"
                        value={metrics.sms_metrics.notice_with_link_success_rate || "0.0%"}
                        icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
                        color="#28a745"
                        bgColor="rgba(40, 167, 69, 0.08)"
                        borderColor="#28a745"
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <MetricTile
                        label="Failure"
                        value={metrics.sms_metrics.notice_with_link_failure_count || 0}
                        icon={<ErrorIcon sx={{ fontSize: 12 }} />}
                        color="#dc3545"
                        bgColor="rgba(220, 53, 69, 0.08)"
                        borderColor="#dc3545"
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <MetricTile
                        label="Failure %"
                        value={metrics.sms_metrics.notice_with_link_failure_rate || "0.0%"}
                        icon={<ErrorIcon sx={{ fontSize: 12 }} />}
                        color="#dc3545"
                        bgColor="rgba(220, 53, 69, 0.08)"
                        borderColor="#dc3545"
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              
              {/* Links Opened Section - Separate from SMS Metrics */}
              <Grid item xs={12}>
                <Box sx={{ 
                  border: '1px solid rgba(0, 0, 0, 0.1)', 
                  borderRadius: 1, 
                  p: 1,
                  backgroundColor: 'rgba(25, 118, 210, 0.03)'
                }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, fontSize: '0.75rem', color: '#1976D2' }}>
                    Links Opened (Distinct Policies)
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <MetricTile
                        label="Policy Document Download"
                        value={metrics.sms_metrics.pdf_links_opened_count || 0}
                        icon={<DescriptionIcon sx={{ fontSize: 12 }} />}
                        color="#1976D2"
                        bgColor="rgba(25, 118, 210, 0.08)"
                        borderColor="#1976D2"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricTile
                        label="Renewal Journey"
                        value={metrics.sms_metrics.renewal_links_opened_count || 0}
                        icon={<LinkIcon sx={{ fontSize: 12 }} />}
                        color="#1976D2"
                        bgColor="rgba(25, 118, 210, 0.08)"
                        borderColor="#1976D2"
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default RenewalMetricsCards;
